import { useState, useEffect, useRef } from 'react';
import fetchAPI from 'app/helpers/fetch';
import { State, PENDING, REJECTED, FULFILLED } from 'admin-mojo/helpers/StateNotification';
import { jumpToTop } from 'brand_xo/pageScroll';
import type { Filters } from 'brand_xo/CasinoFilters/types';
import type { PaginatedGames } from '../types';
import type { GameStarter } from '../useGameStarter';
import { parseGamesPath, buildGamesPath, mergeFilters } from '../gamesFacetPath';

const filterParams = (filters: Filters) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value.length) {
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(`${key}[]`, v));
      } else {
        params.append(key, value);
      }
    }
  });

  return params;
};

const initialFilters = ({ pathname, searchParams }: URL): Filters => {
  const name = searchParams.get('name');
  return {
    ...parseGamesPath(pathname),
    ...(name ? { name } : {}),
  };
};

// The page address of the filters: the facets in the path, the free-text search in the query.
const filtersPath = (filters: Filters) => {
  const query = filters.name ? `?name=${encodeURIComponent(filters.name)}` : '';

  return `${buildGamesPath(filters)}${query}`;
};

const updateAddressBar = (filters: Filters) => {
  window.history.replaceState(null, '', filtersPath(filters));
};

type UseGames = (
  initialPaginatedGames: PaginatedGames,
  gameStarter: GameStarter,
  initialLocation: URL
) => {
  state: State;
  paginatedGames: PaginatedGames;
  // Bumped on every loaded result set, so the grid can start over on it (see Grid's key).
  resultsVersion: number;
  // The search params the games were loaded with, for loading their next pages - the address bar
  // keeps the filters in its path (/games/...), which /casino/search.json doesn't read.
  searchParams: URLSearchParams;
  // The page address of the loaded games (see Grid's next-page link).
  pagePath: string;
  // Whether the filters' path is the homepage (a filter change moves the address bar to
  // /games/..., clearing them back to /).
  homepage: boolean;
};

// eslint-disable-next-line max-lines-per-function
const useGames: UseGames = (initialPaginatedGames, gameStarter, initialLocation) => {
  const [state, setState] = useState<State>({ status: FULFILLED });
  const [filters, setFilters] = useState<Filters>(() => initialFilters(initialLocation));
  const [results, setResults] = useState(() => ({
    paginatedGames: initialPaginatedGames,
    version: 0,
    searchParams: filterParams(filters),
    filters,
  }));
  const initialLoadSkipped = useRef(false);

  const makeSearchRequest = (e: Event) => {
    const { detail } = e as CustomEvent<Filters>;

    // After the game's Back, if any: it brings back the list's scroll position too.
    gameStarter.close().then(() => {
      setFilters((prev) => mergeFilters(prev, detail));
      // The sticky sidebar filters and the pinned filter row can be changed from far down the grid.
      jumpToTop();
    });
  };

  useEffect(() => {
    document.addEventListener('casinoSearch', makeSearchRequest);
    return () => {
      document.removeEventListener('casinoSearch', makeSearchRequest);
    };
  }, []);

  const load = () => {
    setState({ status: PENDING });
    const searchParams = filterParams(filters);

    // Deliberately query-string, not /games/...: that path redirects an empty filter
    // combination to the nearest non-empty facet, which fetch() would follow silently, returning
    // games for a different combination than requested while the address bar stays unchanged.
    // /casino/search.json has no such redirect - it just returns whatever matches, empty or not.
    fetchAPI(`/casino/search.json?${searchParams}`, { method: 'GET', body: null })
      .then((data: PaginatedGames & { title?: string }) => {
        if (data.title) document.title = data.title;
        setResults((prev) => ({ paginatedGames: data, version: prev.version + 1, searchParams, filters }));
        setState({ status: FULFILLED });
      })
      .catch((v) => {
        setState({ status: REJECTED, error: v });
      });
  };

  useEffect(() => {
    if (!initialLoadSkipped.current) {
      // Initial game groups are taken from props. The props are built using search params. No need to load games again.
      initialLoadSkipped.current = true;
      return;
    }

    load();
    updateAddressBar(filters);
  }, [filters]);

  return {
    state,
    paginatedGames: results.paginatedGames,
    resultsVersion: results.version,
    searchParams: results.searchParams,
    pagePath: filtersPath(results.filters),
    homepage: buildGamesPath(filters) === '/',
  };
};

export default useGames;
